import type { Stroke } from '../../types';
import type { Point } from '../diagram/state/types';
import type { ViewportSnapshot } from '../../utils/ViewportControllerBase';
import type { UnifiedStore } from './UnifiedStore';
import type { DrawShape } from '../draw/shapes';
import { fillCanvasBackground } from '../../utils/grid';
import { FONT_FAMILIES } from '../../config/constants';
import { renderStrokeSelection } from '../draw/strokeResize';

interface UnifiedRenderContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  strokeCanvas: HTMLCanvasElement;
  strokeCtx: CanvasRenderingContext2D;
  store: UnifiedStore;
  getViewport: () => ViewportSnapshot;
  getStrokes: () => Stroke[];
  getCurrentStroke: () => Stroke | null;
  getPreviewShape: () => DrawShape | null;
  getStrokesToDelete: () => Set<number>;
  getElementsToDelete: () => Set<string>;
  getSelectedStrokeIds: () => Set<string>;
  getBoxSelectionBounds: () => { x: number; y: number; width: number; height: number } | null;
  getPlaceholderOpacity: () => number;
  getCurrentTool: () => string;
  getSelectionBox: () => { active: boolean; start: Point | null; end: Point | null };
  drawGrid: (width: number, height: number, ctx: CanvasRenderingContext2D) => void;
  drawStrokeToLayer: (stroke: Stroke, ctx: CanvasRenderingContext2D) => void;
  isConnectorToolOrSelected: () => boolean;
}

export class UnifiedRenderPipeline {
  private readonly context: UnifiedRenderContext;

  constructor(context: UnifiedRenderContext) {
    this.context = context;
  }

  render(): void {
    const { canvas, ctx, strokeCanvas, strokeCtx } = this.context;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const viewport = this.context.getViewport();
    const store = this.context.store;

    // 1. Background
    fillCanvasBackground(ctx, width, height, viewport.inverted);

    // Placeholder (diagram mode empty-state hint)
    this.renderPlaceholder(ctx, width, height, viewport.inverted);

    // 2. Grid
    ctx.save();
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(viewport.scale, viewport.scale);
    this.context.drawGrid(width, height, ctx);
    ctx.restore();

    // 3. Draw shapes (behind strokes)
    const drawShapes = store.drawShapes;
    const previewShape = this.context.getPreviewShape();
    if (drawShapes.length > 0 || previewShape) {
      ctx.save();
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(viewport.scale, viewport.scale);

      const elementsToDelete = this.context.getElementsToDelete();
      for (const shape of drawShapes) {
        if (elementsToDelete.has(shape.id)) { ctx.save(); ctx.globalAlpha = 0.3; }
        shape.render(ctx);
        shape.renderSelection(ctx);
        if (elementsToDelete.has(shape.id)) ctx.restore();
      }

      if (previewShape) {
        ctx.save();
        ctx.globalAlpha = 0.6;
        previewShape.render(ctx);
        ctx.restore();
      }

      ctx.restore();
    }

    // 4. Strokes (offscreen canvas for eraser compositing)
    const strokes = this.context.getStrokes();
    const strokesToDelete = this.context.getStrokesToDelete();

    strokeCtx.clearRect(0, 0, width, height);
    strokeCtx.save();
    strokeCtx.translate(viewport.panX, viewport.panY);
    strokeCtx.scale(viewport.scale, viewport.scale);

    for (let i = 0; i < strokes.length; i++) {
      const stroke = strokes[i]!;
      strokeCtx.globalAlpha = strokesToDelete.has(i) ? 0.3 : 1;
      this.context.drawStrokeToLayer(stroke, strokeCtx);
    }

    strokeCtx.globalAlpha = 1;
    const currentStroke = this.context.getCurrentStroke();
    if (currentStroke) {
      this.context.drawStrokeToLayer(currentStroke, strokeCtx);
    }

    strokeCtx.restore();

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(strokeCanvas, 0, 0);
    ctx.restore();

    // 5. Diagram elements — connectors behind shapes
    ctx.save();
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(viewport.scale, viewport.scale);

    const elemToDelete = this.context.getElementsToDelete();
    for (const connector of store.connectors) {
      if (elemToDelete.has(connector.id)) { ctx.save(); ctx.globalAlpha = 0.3; }
      connector.render(ctx, viewport.inverted);
      if (elemToDelete.has(connector.id)) ctx.restore();
    }
    for (const shape of store.shapes) {
      if (elemToDelete.has(shape.id)) { ctx.save(); ctx.globalAlpha = 0.3; }
      shape.render(ctx);
      if (elemToDelete.has(shape.id)) ctx.restore();
    }

    ctx.restore();

    // 6. Text elements
    const textElements = store.textElements;
    if (textElements.length > 0) {
      ctx.save();
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(viewport.scale, viewport.scale);
      const textElemToDelete = this.context.getElementsToDelete();
      for (const textElement of textElements) {
        if (textElemToDelete.has(textElement.id)) { ctx.save(); ctx.globalAlpha = 0.3; }
        textElement.render(ctx);
        textElement.renderSelection(ctx);
        if (textElemToDelete.has(textElement.id)) ctx.restore();
      }
      ctx.restore();
    }

    // 7. Connection points (when connector tool active or connector selected)
    if (this.context.isConnectorToolOrSelected()) {
      ctx.save();
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(viewport.scale, viewport.scale);
      this.renderConnectionPoints(ctx, viewport.inverted);
      ctx.restore();
    }

    // 8. Selected stroke highlights (by id)
    const selectedStrokeIds = this.context.getSelectedStrokeIds();
    if (selectedStrokeIds.size > 0) {
      ctx.save();
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(viewport.scale, viewport.scale);

      for (const stroke of strokes) {
        if (!selectedStrokeIds.has(stroke.id)) continue;
        renderStrokeSelection(ctx, stroke, viewport.scale);
      }

      ctx.restore();
    }

    // 9. Box selection (draw mode style)
    const boxBounds = this.context.getBoxSelectionBounds();
    if (boxBounds) {
      ctx.save();
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(viewport.scale, viewport.scale);

      ctx.strokeStyle = '#3b82f6';
      ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
      ctx.lineWidth = 1 / viewport.scale;
      ctx.setLineDash([4 / viewport.scale, 4 / viewport.scale]);
      ctx.fillRect(boxBounds.x, boxBounds.y, boxBounds.width, boxBounds.height);
      ctx.strokeRect(boxBounds.x, boxBounds.y, boxBounds.width, boxBounds.height);
      ctx.setLineDash([]);
      ctx.restore();
    }

    // 10. Selection box (diagram mode style)
    const selectionBox = this.context.getSelectionBox();
    if (selectionBox.active && selectionBox.start && selectionBox.end) {
      ctx.save();
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(viewport.scale, viewport.scale);
      this.renderSelectionBox(ctx, selectionBox.start, selectionBox.end, viewport.inverted, viewport.scale);
      ctx.restore();
    }
  }

  private renderPlaceholder(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    inverted: boolean,
  ): void {
    const opacity = this.context.getPlaceholderOpacity();
    if (opacity <= 0) return;

    ctx.save();
    const centerX = width / 2;
    const centerY = height / 2;
    const baseOpacity = inverted ? 0.4 : 0.35;
    const finalOpacity = baseOpacity * opacity;

    ctx.fillStyle = inverted
      ? `rgba(255, 255, 255, ${finalOpacity})`
      : `rgba(0, 0, 0, ${finalOpacity})`;
    ctx.font = `16px ${FONT_FAMILIES.ui}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.fillText('Scrawl a box, oval shape, or line to get started.', centerX, centerY - 24);
    ctx.fillText('For a specific shape, select from the sidebar and click the canvas.', centerX, centerY);
    ctx.fillText("Double-click shapes to add text, triple-click elsewhere. You'll figure the rest out.", centerX, centerY + 24);
    ctx.restore();
  }

  private renderConnectionPoints(ctx: CanvasRenderingContext2D, inverted: boolean): void {
    const store = this.context.store;
    const accentColor = inverted ? '#60a5fa' : '#2563eb';
    const accentBg = inverted ? 'rgba(96, 165, 250, 0.2)' : 'rgba(37, 99, 235, 0.15)';
    const activeColor = inverted ? '#4ade80' : '#16a34a';
    const activeBg = inverted ? 'rgba(74, 222, 128, 0.3)' : 'rgba(22, 163, 74, 0.25)';

    const activeConnectionPoints = new Set<string>();
    for (const connector of store.connectors) {
      if (connector.selected) {
        if (connector.startShapeId && connector.startConnectionPointId) {
          activeConnectionPoints.add(`${connector.startShapeId}:${connector.startConnectionPointId}`);
        }
        if (connector.endShapeId && connector.endConnectionPointId) {
          activeConnectionPoints.add(`${connector.endShapeId}:${connector.endConnectionPointId}`);
        }
      }
    }

    ctx.save();
    for (const shape of store.shapes) {
      if (shape.selected && store.connectors.some((c) => c.selected)) continue;

      for (const cp of shape.getConnectionPoints()) {
        const isActive = activeConnectionPoints.has(`${shape.id}:${cp.id}`);
        if (isActive) {
          ctx.fillStyle = activeBg;
          ctx.beginPath();
          ctx.arc(cp.position.x, cp.position.y, 9, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = activeColor;
          ctx.beginPath();
          ctx.arc(cp.position.x, cp.position.y, 5, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = accentBg;
          ctx.beginPath();
          ctx.arc(cp.position.x, cp.position.y, 8, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = accentColor;
          ctx.beginPath();
          ctx.arc(cp.position.x, cp.position.y, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  }

  private renderSelectionBox(
    ctx: CanvasRenderingContext2D,
    start: Point,
    end: Point,
    inverted: boolean,
    scale: number
  ): void {
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);

    ctx.fillStyle = inverted ? 'rgba(96, 165, 250, 0.15)' : 'rgba(37, 99, 235, 0.1)';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = inverted ? '#60a5fa' : '#2563eb';
    ctx.lineWidth = 1 / scale;
    ctx.setLineDash([4 / scale, 4 / scale]);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
  }
}
